import { createAsyncThunk } from "@reduxjs/toolkit";
import { has } from "lodash";
import { Dispatch } from "react";

import { IStoreState } from "App/types";
import { EventService } from "Bands/services";
import { BAND_ACTIONS, EventBlocksType, IEvent } from "Bands/types";
import { API_ENDPOINTS } from "shared/constants";
import { IDataKeys } from "shared/types";
import { isoToUTCStringEvent } from "shared/utils";

import { getCalendarCacheKey } from "../utils/helpers";

export const dispatchGetEvents = (
  dispatch: Dispatch<any>,
  band?: string,
  start?: string,
  stop?: string
) => {
  dispatch(
    getEvents({
      band,
      start,
      stop,
      paginationKey: BAND_ACTIONS.getEvents,
    })
  );
};

export const getEvents = createAsyncThunk(
  BAND_ACTIONS.getEvents,
  async (
    {
      band,
      start,
      stop,
    }: {
      band?: string;
      start?: string;
      stop?: string;
      paginationKey: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventService.getEvents({
        band: band ? API_ENDPOINTS.BAND(band) : undefined,
        start: start ? isoToUTCStringEvent({ iso: start, type: "start" }) : undefined,
        stop: stop ? isoToUTCStringEvent({ iso: stop, type: "end" }) : undefined,
      });

      return {
        [IDataKeys.event]: data.event,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  {
    condition: ({ band, start, stop }, { getState }) => {
      const state = getState() as IStoreState;

      // don't refetch calendar events if already in state
      return !has(state, [
        "bandData",
        "data",
        IDataKeys.calendar,
        getCalendarCacheKey(band, start, stop),
      ]);
    },
  }
);

export const dispatchGetEventAction = (
  dispatch: Dispatch<any>,
  eventId: string,
  force?: boolean
) => {
  return dispatch(getEvent({ eventId, force }));
};

export const getEvent = createAsyncThunk(
  BAND_ACTIONS.getEvent,
  async ({ eventId }: { eventId: string; force?: boolean }, { rejectWithValue }) => {
    try {
      const event = await EventService.getEvent(eventId);

      return event;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  // Do not fetch the event if it is already in the state.
  // The event state gets updated on delete/edit actions
  {
    condition: ({ eventId, force }, { getState }) => {
      if (force) return force;

      const state = getState() as IStoreState;
      const { event } = state.bandData.data;
      const path = API_ENDPOINTS.EVENT(eventId);

      return !has(event, path);
    },
  }
);

export const createEvent = createAsyncThunk(
  BAND_ACTIONS.createEvent,
  async (event: Omit<IEvent, "id">, { rejectWithValue }) => {
    try {
      const data = await EventService.createEvent(event);
      const id = Object.values(data?.event)?.[0]?.attributes?.id;
      return { id, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editEvent = createAsyncThunk(
  BAND_ACTIONS.editEvent,
  async (
    props: {
      id: string;
      data: Partial<IEvent>;
      blockTypesToUpdate?: EventBlocksType[];
      resendRSVP?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventService.editEvent(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteEvent = createAsyncThunk(
  BAND_ACTIONS.deleteEvent,
  async (id: string, { rejectWithValue }) => {
    try {
      await EventService.deleteEvent(id);
      return { path: API_ENDPOINTS.EVENT(id), dataKey: IDataKeys.event };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
